import fetchAccountWorkflows from '@Application/SubjectTypeDefinitions/fetchAccountWorkflows';
import fetchInformationModes from '@Application/SubjectTypeDefinitions/fetchInformationModes';
import fetchSubjectAbouts from '@Application/SubjectTypeDefinitions/fetchSubjectAbouts';
import fetchSubjectFieldNatures from '@Application/SubjectTypeDefinitions/fetchSubjectFieldNatures';
import fetchSubjectTypeColors from '@Application/SubjectTypeDefinitions/fetchSubjectTypeColors';
import fetchSubjectTypeIcons from '@Application/SubjectTypeDefinitions/fetchSubjectTypeIcons';
import fetchSubjectTypeAccessLevels from '@Application/SubjectTypeDefinitions/fetchSubjectTypeAccessLevels';
import FetchAccountWorkflows from '@Application/SubjectTypeDefinitions/Queries/FetchAccountWorkflows';
import FetchInformationModes from '@Application/SubjectTypeDefinitions/Queries/FetchInformationModes';
import FetchSubjectAbouts from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectAbouts';
import FetchSubjectFieldNatures from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectFieldNatures';
import FetchSubjectTypeColors from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectTypeColors';
import FetchSubjectTypeIcons from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectTypeIcons';
import FetchSubjectTypeAccessLevels from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectTypeAccessLevels';
import * as React from 'react';
import { STATIC_CACHE, useDispatch, useStoreQuery } from '@verclam/synapse';

export type SubjectTypeEditorReferences = {
	/** True once the workflows and the six enumerations are in the store. */
	readonly isReady: boolean;
	readonly hasFailed: boolean;
	readonly refetch: () => void;
};

/**
 * Loads what the editor sections read from the store: the workflows of the account (short cache: a
 * return within a minute reuses the store; past that the previous value stays on screen while it is
 * read again) and the six enumerations (natures, populations, information modes, access levels, icons,
 * colors), cached as static data. Sections select these slots themselves; the editor only waits for them.
 */
function useSubjectTypeEditorReferences(accountId: number | null): SubjectTypeEditorReferences {
	const dispatch = useDispatch();

	const workflowsQuery = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.workflows,
		stopOn: (value) => accountId === null || (value !== null && value.accountId === accountId),
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAccountWorkflows(new FetchAccountWorkflows(accountId)));
		},
		deps: [accountId],
	});

	const naturesQuery = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.fieldNatures,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchSubjectFieldNatures(new FetchSubjectFieldNatures())),
		cache: STATIC_CACHE,
	});

	const aboutsQuery = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.abouts,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchSubjectAbouts(new FetchSubjectAbouts())),
		cache: STATIC_CACHE,
	});

	const informationModesQuery = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.informationModes,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchInformationModes(new FetchInformationModes())),
		cache: STATIC_CACHE,
	});

	const accessLevelsQuery = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.accessLevels,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchSubjectTypeAccessLevels(new FetchSubjectTypeAccessLevels())),
		cache: STATIC_CACHE,
	});

	const iconsQuery = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.icons,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchSubjectTypeIcons(new FetchSubjectTypeIcons())),
		cache: STATIC_CACHE,
	});

	const colorsQuery = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.colors,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchSubjectTypeColors(new FetchSubjectTypeColors())),
		cache: STATIC_CACHE,
	});

	const queries = [
		workflowsQuery,
		naturesQuery,
		aboutsQuery,
		informationModesQuery,
		accessLevelsQuery,
		iconsQuery,
		colorsQuery,
	];

	const refetchWorkflows = workflowsQuery.refetch;
	const refetchNatures = naturesQuery.refetch;
	const refetchAbouts = aboutsQuery.refetch;
	const refetchInformationModes = informationModesQuery.refetch;
	const refetchAccessLevels = accessLevelsQuery.refetch;

	const refetchIcons = iconsQuery.refetch;
	const refetchColors = colorsQuery.refetch;

	const refetch = React.useCallback(() => {
		refetchWorkflows();
		refetchNatures();
		refetchAbouts();
		refetchInformationModes();
		refetchAccessLevels();
		refetchIcons();
		refetchColors();
	}, [
		refetchWorkflows,
		refetchNatures,
		refetchAbouts,
		refetchInformationModes,
		refetchAccessLevels,
		refetchIcons,
		refetchColors,
	]);

	return {
		isReady: queries.every((query) => query.data !== null),
		hasFailed: queries.some((query) => query.data === null && query.error !== null),
		refetch,
	};
}

export default useSubjectTypeEditorReferences;
